import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWsUrl } from '../../utils/api-url';
import { useGameStore } from '../../store/gameStore';
import { playerAPI, gameAPI } from '../../services/api';
import AvatarCapture from './AvatarCapture';
import ShareLink from './ShareLink';
import GenerationProgress from '../ui/GenerationProgress';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function PlayerSetup() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { currentGame, players, addPlayer, setPlayers, setLoading, setError, setSetupStep, isLoading, saveDraftAndExit } = useGameStore();
  const [playerName, setPlayerName] = useState('');
  const [currentPlayerForAvatar, setCurrentPlayerForAvatar] = useState<string | null>(null);
  const [isGeneratingWorld, setIsGeneratingWorld] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [currentGenerationStep, setCurrentGenerationStep] = useState<string | undefined>(undefined);
  // Inline rename of a roster entry before the world is generated.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // WebSocket connection for progress updates
  useWebSocket({
    gameId: currentGame?.id,
    onTurnCompleted: () => {},
  });

  useEffect(() => {
    if (!currentGame) return;

    let socket: any = null;

    // Listen for world generation progress and intro ready
    const handleProgress = (data: { step: string }) => {
      setCurrentGenerationStep(data.step);
    };

    const handleIntroReady = (_data: { narrative: string; imageId: string }) => {
      console.log('✅ Intro ready, entering the game (first scene is the opening page of the book)…');
      // Go straight into the game — the intro/opening scene is shown as the book's first page,
      // where the player can read it AND act, rather than a separate intro screen.
      const playerId = localStorage.getItem(`game_${currentGame.id}_player`);
      const url = playerId ? `/game/${currentGame.id}?playerId=${playerId}` : `/game/${currentGame.id}`;
      window.location.href = url;
    };

    // Import socket.io-client and listen for events
    import('socket.io-client').then(({ io }) => {
      const wsUrl = getWsUrl();
      socket = io(wsUrl);

      socket.emit('game:join', { gameId: currentGame.id });
      socket.on('world:generation:progress', handleProgress);
      socket.on('game:intro:ready', handleIntroReady);
    });

    return () => {
      if (socket) {
        socket.off('world:generation:progress', handleProgress);
        socket.off('game:intro:ready', handleIntroReady);
        socket.disconnect();
      }
    };
  }, [currentGame?.id]);

  // While assembling the roster, refresh the player list so people who join via the invite link
  // appear here live (and drop-offs disappear) — no reload needed. Paused during generation and
  // while the host is editing a name, so it doesn't fight those.
  useEffect(() => {
    const id = currentGame?.id;
    // Only multiplayer games expect people to join via a link.
    if (!id || (currentGame?.max_players || 1) <= 1 || isGeneratingWorld || editingId) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const g = await gameAPI.getGame(id);
        if (!cancelled && Array.isArray(g.players)) setPlayers(g.players);
      } catch { /* transient */ }
    };
    const iv = setInterval(refresh, 4000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.id, isGeneratingWorld, editingId]);

  if (!currentGame) return null;

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!playerName.trim()) {
      setError(t('players.enterPlayerName'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const player = await playerAPI.addPlayer(currentGame.id, playerName);
      addPlayer(player);
      setPlayerName('');
    } catch (error: any) {
      setError(error.response?.data?.error || t('players.addError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayer = async (playerId: string) => {
    try {
      setError(null);
      await playerAPI.deletePlayer(currentGame.id, playerId);
      setPlayers(players.filter(p => p.id !== playerId));
    } catch (error: any) {
      setError(error.response?.data?.error || t('players.removeError'));
    }
  };

  const roundBtn = (bg: string, color: string): React.CSSProperties => ({
    padding: '0.5rem 0.8rem', fontSize: '0.9rem', border: 'none', borderRadius: '8px',
    background: bg, color, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
  });

  const startEdit = (p: any) => { setEditingId(p.id); setEditName(p.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };
  const saveEdit = async (playerId: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      setError(null);
      const updated = await playerAPI.updatePlayer(currentGame.id, playerId, name);
      setPlayers(players.map(p => (p.id === playerId ? { ...p, name: updated.name } : p)));
      cancelEdit();
    } catch (error: any) {
      setError(error.response?.data?.error || t('players.renameError'));
    }
  };

  const handleGenerateWorld = async () => {
    if (players.length === 0) {
      setError(t('players.addAtLeastOne'));
      return;
    }

    try {
      setIsGeneratingWorld(true);
      setError(null);
      setGenerationError(null);

      // Generate world with Claude
      const worldData = await gameAPI.generateWorld(currentGame.id);

      // Assign roles to players
      await playerAPI.assignRoles(currentGame.id);

      // Reload players with roles
      const updatedGame = await gameAPI.getGame(currentGame.id);
      setPlayers(updatedGame.players);

      setSetupStep('generate-world');
    } catch (error: any) {
      const status = error.response?.status;
      const errorMessage = error.response?.data?.error || error.message || t('players.generateError');

      // The world is already generated for this game (stale setup screen) — just
      // take the player into the game instead of showing an error.
      if (status === 400 && /already generated|already started|уже сгенерирован/i.test(errorMessage)) {
        const playerId = localStorage.getItem(`game_${currentGame.id}_player`);
        window.location.href = playerId
          ? `/game/${currentGame.id}?playerId=${playerId}`
          : `/game/${currentGame.id}`;
        return;
      }

      setError(errorMessage);
      setGenerationError(errorMessage);
    } finally {
      setIsGeneratingWorld(false);
    }
  };

  const canProceed = players.length > 0 && players.length <= currentGame.max_players;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto 6rem auto', padding: isMobile ? '0 0.75rem' : undefined, animation: 'fadeIn 0.6s ease-out' }}>
      {/* Leave to the home menu — the in-progress game is saved as a draft to resume later */}
      <button
        onClick={saveDraftAndExit}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          margin: '0.5rem 0 1rem',
          padding: '0.5rem 0.9rem',
          borderRadius: '10px',
          border: '1px solid rgba(148, 163, 184, 0.3)',
          background: 'rgba(148, 163, 184, 0.1)',
          color: '#cbd5e0',
          fontSize: '0.85rem',
          cursor: 'pointer',
        }}
      >
        ← {t('setup.exitToMenu')}
      </button>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: isMobile ? '1.5rem' : '2.5rem',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {t('players.title')}
        </h1>
        <p style={{ fontSize: '1rem', color: '#a0aec0' }}>
          {currentGame.name} • {t('players.maxPlayers', { count: currentGame.max_players })}
        </p>
      </div>

      {/* Share Link */}
      {currentGame.share_code && <ShareLink shareCode={currentGame.share_code} />}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1rem' : '2rem' }}>
        {/* Left Column - Add Player Form */}
        <div style={{
          background: 'rgba(15, 12, 41, 0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          padding: isMobile ? '1.25rem' : '2rem',
        }}>
          <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.5rem', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            ➕ {t('players.newPlayer')}
          </h2>

          <form onSubmit={handleAddPlayer}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#e2e8f0',
              }}>
                {t('players.playerNameLabel')}
              </label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder={t('players.playerNamePlaceholder')}
                disabled={isLoading || players.length >= currentGame.max_players}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  fontSize: '1rem',
                  border: '2px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '10px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                  e.target.style.boxShadow = '0 0 15px rgba(99, 102, 241, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || players.length >= currentGame.max_players}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '10px',
                background: (isLoading || players.length >= currentGame.max_players)
                  ? 'rgba(99, 102, 241, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                cursor: (isLoading || players.length >= currentGame.max_players) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                if (!isLoading && players.length < currentGame.max_players) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isLoading ? `⏳ ${t('players.adding')}` : `✅ ${t('players.addPlayer')}`}
            </button>
          </form>

          {players.length >= currentGame.max_players && (
            <p style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              color: '#fbbf24',
              fontSize: '0.875rem',
              textAlign: 'center',
            }}>
              ⚠️ {t('players.maxReached')}
            </p>
          )}
        </div>

        {/* Right Column - Players List */}
        <div style={{
          background: 'rgba(15, 12, 41, 0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          padding: isMobile ? '1.25rem' : '2rem',
        }}>
          <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.5rem', marginBottom: '0.5rem', color: '#e2e8f0' }}>
            👥 {t('players.listTitle')} ({players.length}/{currentGame.max_players})
          </h2>
          <p style={{ fontSize: '0.8rem', color: '#8b93a7', margin: '0 0 1.25rem' }}>
            🔄 {t('players.autoUpdateHint')}
          </p>

          {players.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: '#718096',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎭</div>
              <p>{t('players.emptyRoster')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {players.map((player, index) => (
                <div
                  key={player.id}
                  style={{
                    padding: '1rem',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    animation: 'slideInRight 0.3s ease-out',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {editingId === player.id ? (
                        <input
                          autoFocus
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(player.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          style={{
                            width: '100%', padding: '0.45rem 0.65rem', fontSize: '0.95rem',
                            borderRadius: '8px', border: '2px solid rgba(99,102,241,0.6)',
                            background: 'rgba(0,0,0,0.35)', color: '#fff', outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <>
                          <div style={{ fontWeight: '600', color: '#e2e8f0' }}>{player.name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#a0aec0' }}>
                            {player.avatar_data?.photos?.length ? `📸 ${t('players.photosUploaded')}` : `⚪ ${t('players.noPhoto')}`}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    {editingId === player.id ? (
                      <>
                        <button onClick={() => saveEdit(player.id)} title={t('common.save')}
                          style={roundBtn('rgba(16,185,129,0.25)', '#6ee7b7')}>✔</button>
                        <button onClick={cancelEdit} title={t('common.cancel')}
                          style={roundBtn('rgba(148,163,184,0.15)', '#cbd5e0')}>✖</button>
                      </>
                    ) : (
                      <>
                        {!player.avatar_data?.photos?.length && (
                          <button onClick={() => setCurrentPlayerForAvatar(player.id)} title={t('players.addPhotoTitle')}
                            style={roundBtn('rgba(99,102,241,0.2)', '#fff')}>📷</button>
                        )}
                        <button onClick={() => startEdit(player)} title={t('players.renameTitle')}
                          style={roundBtn('rgba(99,102,241,0.2)', '#c7d2fe')}>✏️</button>
                        <button onClick={() => handleRemovePlayer(player.id)} title={t('common.delete')}
                          style={roundBtn('rgba(239,68,68,0.12)', '#fca5a5')}>❌</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate World Button */}
      {canProceed && (
        <div style={{
          marginTop: '2rem',
          padding: isMobile ? '1.25rem' : '2rem',
          background: 'rgba(15, 12, 41, 0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          textAlign: 'center',
          animation: 'fadeIn 0.5s ease-out',
        }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#e2e8f0' }}>
            ✨ {t('players.readyTitle')}
          </h3>
          <p style={{ color: '#a0aec0', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            {t('players.readyDesc')}
          </p>
          <button
            onClick={handleGenerateWorld}
            disabled={isGeneratingWorld}
            style={{
              width: isMobile ? '100%' : undefined,
              boxSizing: 'border-box',
              padding: isMobile ? '1rem 1.25rem' : '1.25rem 3rem',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '12px',
              background: isGeneratingWorld
                ? 'rgba(99, 102, 241, 0.3)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              cursor: isGeneratingWorld ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!isGeneratingWorld) {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(16, 185, 129, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(16, 185, 129, 0.3)';
            }}
          >
            {isGeneratingWorld ? `🎲 ${t('players.generating')}` : `🚀 ${t('players.generateAndStart')}`}
          </button>
        </div>
      )}

      {/* Avatar Capture Modal */}
      {currentPlayerForAvatar && (
        <AvatarCapture
          playerId={currentPlayerForAvatar}
          playerName={players.find(p => p.id === currentPlayerForAvatar)?.name || ''}
          gameId={currentGame.id}
          onClose={() => setCurrentPlayerForAvatar(null)}
          onSaved={() => {
            setCurrentPlayerForAvatar(null);
            // Reload players to show updated avatar status
            gameAPI.getGame(currentGame.id).then(game => {
              setPlayers(game.players);
            });
          }}
        />
      )}

      {/* World Generation Progress */}
      <GenerationProgress
        isGenerating={isGeneratingWorld}
        currentStep={currentGenerationStep}
        error={generationError}
      />
    </div>
  );
}
